export type EntityKind = "customers" | "items" | "orders";

export interface EntitySearchIntent {
	command: boolean;
	entity: EntityKind | null;
	explicit: boolean;
	isOrderNumber: boolean;
	query: string;
}

export const COMMAND_PREFIX = ">";

const SPACE_PREFIXES: Record<string, EntityKind> = {
	c: "customers",
	i: "items",
	o: "orders"
};

const COLON_PREFIXES: Record<string, EntityKind> = {
	c: "customers",
	customer: "customers",
	i: "items",
	item: "items",
	o: "orders",
	order: "orders"
};

export const ORDER_NUMBER_PATTERN = /^#?\d{4,}$/;

export const looksLikeOrderNumber = (value: string): boolean => ORDER_NUMBER_PATTERN.test(value.trim());

export const replaceEntityQuery = (raw: string, intent: EntitySearchIntent, query: string): string => {
	const queryStart = intent.query ? raw.lastIndexOf(intent.query) : -1;
	return queryStart === -1 ? query : `${raw.slice(0, queryStart)}${query}`;
};

const buildIntent = (entity: EntityKind, query: string): EntitySearchIntent => ({
	command: false,
	entity,
	explicit: true,
	isOrderNumber: false,
	query: query.trim()
});

const parseEntitySearch = (raw: string): EntitySearchIntent => {
	const trimmed = raw.trim();
	if (!trimmed) {
		return { command: false, entity: null, explicit: false, isOrderNumber: false, query: "" };
	}

	if (trimmed.startsWith(COMMAND_PREFIX)) {
		return {
			command: true,
			entity: null,
			explicit: true,
			isOrderNumber: false,
			query: trimmed.slice(COMMAND_PREFIX.length).trim()
		};
	}

	const firstSpace = trimmed.search(/\s/);
	const firstWord = firstSpace === -1 ? trimmed : trimmed.slice(0, firstSpace);
	const rest = firstSpace === -1 ? "" : trimmed.slice(firstSpace + 1).trim();

	const colonIndex = firstWord.indexOf(":");
	if (colonIndex > 0) {
		const colonEntity = COLON_PREFIXES[firstWord.slice(0, colonIndex).toLowerCase()];
		if (colonEntity) {
			return buildIntent(colonEntity, [firstWord.slice(colonIndex + 1), rest].filter(Boolean).join(" "));
		}
	}

	const spaceEntity = SPACE_PREFIXES[firstWord.toLowerCase()];
	if (spaceEntity && rest) {
		return buildIntent(spaceEntity, rest);
	}

	return {
		command: false,
		entity: null,
		explicit: false,
		isOrderNumber: looksLikeOrderNumber(trimmed),
		query: trimmed
	};
};

export default parseEntitySearch;
